import React, { useState, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import Badge from "aldoo-ra/Badge"
import Tooltip from "aldoo-ra/Tooltip"

import apiInterface from "aldoo-ra/CMSModels/ProductPurchase/api"
const { GetDeleted, Restore, HardDelete } = apiInterface

export default function ProductPurchaseRestoreDialog({ isOpen, onClose, onRefresh }) {
  const [refreshKey, setRefreshKey] = useState(0)

  // Refresh the table when dialog opens
  useEffect(() => {
    if (!isOpen) return
    setRefreshKey((prev) => prev + 1)
  }, [isOpen])

  // Handler for deleting an item forever
  const deleteForeverAction = (item) => {
    showPromptDialog({
      title: "Delete Forever",
      message: `Are you sure you want to permanently delete purchase for ${item.pid}? This action cannot be undone.`,
      confirmText: "Delete Forever",
      onConfirm: async () => {
        try {
          await HardDelete(item) // Call HardDelete with item
          showNotification({
            message: "Purchase permanently deleted successfully.",
            className: "bg-admin_primary text-admin_text_inv",
          })
          // Force refresh the restore dialog table
          setTimeout(() => {
            setRefreshKey((prev) => prev + 1)
          }, 50)
          onRefresh?.() // Refresh the main table
        } catch (error) {
          showNotification({
            message: "Failed to delete the purchase.",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handler for restoring an item
  const restoreAction = async (item) => {
    try {
      await Restore(item._id) // Restore purchase
      showNotification({
        message: "Successfully restored the purchase.",
        className: "bg-admin_primary text-admin_text_inv",
      })
      // Force refresh the restore dialog table
      setTimeout(() => {
        setRefreshKey((prev) => prev + 1)
      }, 50)
      onRefresh?.() // Refresh the main table
    } catch (error) {
      showNotification({
        message: "Failed to restore the purchase.",
        className: "bg-admin_error text-white",
      })
    }
  }

  const formatDate = (date) => {
    if (!date) return "N/A"
    try {
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return "Invalid date"
    }
  }

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "active": return "success"
      case "refunded": return "warning"
      case "revoked": return "error"
      default: return "default"
    }
  }

  // Define columns for the data table
  const tableItemRender = [
    {
      id: "user",
      title: "User",
      headerClassName: "w-[20%]",
      render: (value, item) => (
        <Tooltip content={item.user?.email || "No email"}>
          <span className="truncate">
            {item.user?.name || item.user?.email || "Unknown User"}
          </span>
        </Tooltip>
      ),
    },
    {
      id: "pid",
      title: "Product ID",
      headerClassName: "w-[15%]",
      render: (value, item) => (
        <span>{item.pid}</span>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[10%] text-center",
      render: (value, item) => (
        <div className="w-full flex justify-center">
          <Badge variant={getStatusBadgeVariant(item.status)}>
            {item.status}
          </Badge>
        </div>
      ),
    },
    {
      id: "platform",
      title: "Platform",
      headerClassName: "w-[10%] text-center",
      render: (value, item) => (
        <span className="block text-center">
          {item.platform || "N/A"}
        </span>
      ),
    },
    {
      id: "purchase_date",
      title: "Purchase Date",
      headerClassName: "w-[15%] whitespace-nowrap",
      render: (value, item) => (
        <div className="flex flex-col text-sm">
          <span>
            {formatDate(item.purchase_date)}
          </span>
          {item.created && item.created !== item.purchase_date && (
            <span className="text-xs text-admin_text_secondary">
              Created: {formatDate(item.created)}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "price",
      title: "Price",
      headerClassName: "w-[10%]",
      render: (value, item) => {
        const priceModel = item.price_model;
        
        if (!priceModel) return <span className="text-admin_secondary">No price</span>;
        
        return (
          <div className="flex flex-col text-sm">
            {priceModel.amount && priceModel.currency && (
              <span className="text-admin_text">
                {priceModel.currency} {priceModel.amount}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "restore",
      headerClassName: "w-[10%]",
      render: (value, item) => (
        <Button
          variant="admin_secondary"
          className="ml-auto"
          onClick={() => restoreAction(item)}
        >
          Restore
        </Button>
      ),
    },
    {
      id: "delete",
      headerClassName: "w-[15%]",
      render: (value, item) => (
        <Button
          variant="admin_error"
          className="whitespace-nowrap"
          onClick={() => deleteForeverAction(item)}
        >
          Delete Forever
        </Button>
      ),
    },
  ]

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      className="md:absolute w-full md:w-[95%] h-full md:h-auto top-[15px]"
      zIndex={200}
    >
      <div className="p-4">
        <Typography variant="admin_h3" className="mb-4">
          Restore Deleted Purchases
        </Typography>

        {/* Data table for displaying deleted items */}
        <div className="h-[75vh]">
          {" "}
          {/* Set a minimum height */}
          <DataTable
            variant="admin"
            refreshKey={refreshKey}
            apiMethod={GetDeleted}
            preferredHeight="75vh"
            itemHeight="h-[70px]"
            localUserPreferencesKey="admin.store.restorePurchases"
        styleOverrides={{
              searchInput: "absolute top-[45px] right-[40px] w-full max-w-[300px] xl:max-w-[360px] ml-6 xl:ml-8"
            }}
            searchField="pid user.name user.email platform status"
            requestFields={{ 
              populate: ["user"]
            }}
            searchObject={{}}
            sort={{ deletedAt: -1 }}
            renders={tableItemRender}
            noData={
              <div className="min-h-[400px] flex items-center justify-center">
                <Typography variant="paragraph" className="text-admin_text">
                  No deleted purchases found
                </Typography>
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  )
}